// Swiping between the phone's tabs (components/shell/AppShell): the page
// follows the finger sideways, the title strip slides along, and on release
// it goes to the neighbouring tab or springs back. Pure, so the feel is
// tested without a browser; hooks/useTabSwipe.ts wires it to pointer events.

/** How far the finger moves before the swipe decides whether it is sideways at all. */
export const AXIS_PX = 10
/** Sideways only when clearly more sideways than vertical: a sloppy scroll stays a scroll. */
const AXIS_RATIO = 1.5
/** Past the first or the last tab the page moves a third as far as the finger. */
const EDGE_RESISTANCE = 1 / 3
/** A drag past this share of the width changes tab on release… */
const DISTANCE_SHARE = 0.25
/** …and so does a flick this fast (px per ms), once it has moved a little. */
const FLICK_SPEED = 0.5
const FLICK_MIN_PX = 24

export function axisOf(dx: number, dy: number): 'x' | 'y' | null {
  if (Math.hypot(dx, dy) < AXIS_PX) return null
  return Math.abs(dx) > AXIS_RATIO * Math.abs(dy) ? 'x' : 'y'
}

/** Where the page sits for a drag of `dx` on tab `index` of `count`. */
export function dragOffset(dx: number, index: number, count: number): number {
  const blocked = (dx > 0 && index === 0) || (dx < 0 && index === count - 1)
  return blocked ? dx * EDGE_RESISTANCE : dx
}

/** The tab a drag of `dx` over `ms` lands on. */
export function releaseTo(dx: number, ms: number, width: number, index: number, count: number): number {
  const far = Math.abs(dx) > width * DISTANCE_SHARE
  const flick = Math.abs(dx) >= FLICK_MIN_PX && Math.abs(dx) / Math.max(ms, 1) > FLICK_SPEED
  if (!far && !flick) return index
  return Math.max(0, Math.min(count - 1, index + (dx < 0 ? 1 : -1)))
}

/**
 * How far to shift the title strip: the open title's left edge, moved toward
 * its neighbour by `progress` (-1…1, positive toward the next tab).
 */
export function stripX(lefts: number[], index: number, progress: number): number {
  const to = index + Math.sign(progress)
  if (progress === 0 || to < 0 || to >= lefts.length) return lefts[index] ?? 0
  return lefts[index] + (lefts[to] - lefts[index]) * Math.abs(progress)
}
